import type { GameDefinition } from './types/game';
import { SaveManager } from './engine/SaveManager';
import { mountAdventureEngine, type AdventureEngineHandle } from './mount';
import { el, clear, button } from './render/dom';

import faroJson from './content/faro-santa-clara.json';
import invernaderoJson from './content/la-ultima-luz-invernadero.json';
import guardianesJson from './content/guardianes-cuatro-torres.json';

/** Instancia por defecto; un portal podría pasar otra distinta. */
const DEFAULT_INSTANCE_ID = 'default';

/**
 * Aventuras generadas por el pipeline OA→plantilla. Se descubren solas: cualquier
 * `src/content/oa-*.json` que produzca el ensamblador aparece en el selector sin
 * tocar código (ver tools/oa/assemble.ts e INTEGRACION-LUMBRE.md).
 */
const oaModules = import.meta.glob('./content/oa-*.json', { eager: true }) as Record<
  string,
  { default: GameDefinition }
>;
const oaDemos: AdventureEntry[] = Object.entries(oaModules).map(([path, mod]) => ({
  id: path.split('/').pop()?.replace(/\.json$/, '') ?? path,
  def: mod.default
}));

interface AdventureEntry {
  id: string;
  def: GameDefinition;
}

/** Metadata devuelta por GET /api/adventures. */
interface ApiAdventureMeta {
  id: string;
  title: string;
  description: string;
  author?: string;
  language?: string;
  cover?: string;
}

/**
 * Shell de demostración: selector de aventuras + portada.
 *
 * Consulta el catálogo de la API para aventuras publicadas, mantiene un
 * registro estático de demos y permite cargar un JSON local. Soporta
 * deep-linking vía ?adventure=<id>.
 */
export class App {
  /** Demos embebidas (siempre disponibles aunque no haya API). */
  private readonly demos: AdventureEntry[] = [
    { id: 'faro', def: faroJson as unknown as GameDefinition },
    { id: 'invernadero', def: invernaderoJson as unknown as GameDefinition },
    { id: 'guardianes', def: guardianesJson as unknown as GameDefinition },
    ...oaDemos,
  ];
  /** Aventuras publicadas obtenidas de la API. */
  private readonly apiAdventures: AdventureEntry[] = [];
  private handle: AdventureEngineHandle | null = null;
  private readonly reducedMotion: boolean;

  constructor(private readonly rootEl: HTMLElement) {
    this.reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  start(): void {
    const urlAdventure = this.getUrlAdventureId();
    if (urlAdventure) {
      const demo = this.demos.find((d) => d.id === urlAdventure);
      if (demo) {
        this.setUrlAdventure(urlAdventure);
        this.renderCover(demo.def);
      } else {
        this.loadFromUrl(urlAdventure);
      }
    } else {
      this.renderSelector();
    }
  }

  private get instanceId(): string {
    return DEFAULT_INSTANCE_ID;
  }

  private saveManagerFor(def: GameDefinition): SaveManager {
    return new SaveManager(def, this.instanceId);
  }

  /* ------------------------- URL routing ------------------------- */

  private getUrlAdventureId(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('adventure');
  }

  private setUrlAdventure(adventureId: string | null): void {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (adventureId) {
      url.searchParams.set('adventure', adventureId);
    } else {
      url.searchParams.delete('adventure');
    }
    window.history.replaceState({}, '', url.toString());
  }

  /* ------------------------- carga desde API ------------------------- */

  private async loadFromUrl(adventureId: string): Promise<void> {
    this.destroyHandle();
    clear(this.rootEl);

    const wrap = el('div', { className: 'ae-root', attrs: { style: 'overflow:auto;padding:24px' } });
    const card = el('div', { className: 'ae-modal' });
    card.append(el('h1', { textContent: 'Cargando aventura…' }));
    card.append(el('p', { className: 'lead', textContent: `Obteniendo "${adventureId}" desde el servidor.` }));
    wrap.append(el('div', { className: 'ae-modal-backdrop', attrs: { style: 'position:static;background:none;padding:0' } }, [card]));
    this.rootEl.append(wrap);

    try {
      const response = await fetch(`/api/adventures/${encodeURIComponent(adventureId)}?reroll=1`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const msg = errorData?.message ?? `HTTP ${response.status}`;
        this.renderError(
          `No se pudo cargar "${adventureId}"`,
          msg,
          () => this.renderSelector()
        );
        return;
      }
      const def = (await response.json()) as GameDefinition;
      this.setUrlAdventure(adventureId);
      this.renderCover(def);
    } catch (err) {
      this.renderError(
        `Error de red al cargar "${adventureId}"`,
        String((err as Error).message ?? err),
        () => this.renderSelector()
      );
    }
  }

  /* ------------------------- selector ------------------------- */

  private renderSelector(): void {
    this.destroyHandle();
    clear(this.rootEl);
    this.setUrlAdventure(null);

    const wrap = el('div', { className: 'ae-root' });
    const homepage = el('div', { className: 'ae-homepage' });

    // Hero section
    const hero = el('div', { className: 'ae-hero' });
    const heroContent = el('div', { className: 'ae-hero-content' });
    
    const badge = el('div', { className: 'ae-hero-badge', textContent: 'Motor de aventuras educativas' });
    const title = el('h1', { textContent: 'adventurengine' });
    const subtitle = el('p', { 
      className: 'ae-hero-subtitle',
      textContent: 'Aventuras gráfico-conversacionales definidas por JSON. Sin código, solo narrativa, decisiones y consecuencias.'
    });
    
    const stats = el('div', { className: 'ae-hero-stats' });
    stats.append(
      this.createStat('4', 'Aventuras'),
      this.createStat('122', 'Tests'),
      this.createStat('100%', 'Open Source')
    );
    
    heroContent.append(badge, title, subtitle, stats);
    hero.append(heroContent);
    homepage.append(hero);

    // Adventures section
    const adventuresSection = el('div', { className: 'ae-section' });
    const sectionHeader = el('div', { className: 'ae-section-header' });
    sectionHeader.append(
      el('h2', { textContent: 'Elige tu aventura' }),
      el('p', { textContent: 'Cada historia es un mundo por descubrir. Tus decisiones importan.' })
    );
    adventuresSection.append(sectionHeader);

    const grid = el('div', { className: 'ae-adventure-grid' });
    adventuresSection.append(grid);
    homepage.append(adventuresSection);

    // Loading state
    const loadingState = el('div', { className: 'ae-loading' });
    const spinner = el('div', { className: 'ae-loading-spinner' });
    const loadingText = el('p', { textContent: 'Cargando aventuras…' });
    loadingState.append(spinner, loadingText);
    grid.append(loadingState);

    // Upload section
    const uploadSection = el('div', { className: 'ae-section' });
    const uploadCard = el('div', { className: 'ae-upload-section' });
    uploadCard.append(
      el('h3', { textContent: '¿Tienes tu propia aventura?' }),
      el('p', { textContent: 'Carga un JSON compatible y juega al instante. Sin instalación, sin configuración.' })
    );
    const uploadLabel = el('label', { className: 'ae-upload-input' });
    uploadLabel.textContent = ' Cargar JSON local';
    const fileInput = el('input', { type: 'file', attrs: { accept: 'application/json,.json' } });
    fileInput.addEventListener('change', () => this.onLocalFile(fileInput));
    uploadLabel.append(fileInput);
    uploadCard.append(uploadLabel);
    uploadSection.append(uploadCard);
    homepage.append(uploadSection);

    // Features section
    const featuresSection = el('div', { className: 'ae-section' });
    const featuresHeader = el('div', { className: 'ae-section-header' });
    featuresHeader.append(
      el('h2', { textContent: 'Diseñado para aprender' }),
      el('p', { textContent: 'Cada mecánica al servicio del pensamiento crítico' })
    );
    
    const features = el('div', { className: 'ae-features' });
    features.append(
      this.createFeature('🎯', 'Decisiones reales', 'Cada elección tiene consecuencias perceptibles. No hay respuestas correctas ocultas.'),
      this.createFeature('', 'Evidencias, no suposiciones', 'Reúne pruebas, evalúa su calidad y construye conclusiones proporcionales.'),
      this.createFeature('🔄', 'Múltiples finales', '8 desenlaces posibles según tu razonamiento. Rejuega para explorar caminos alternativos.'),
      this.createFeature('', 'Feedback educativo', 'Al final, descubre qué hiciste bien, qué te faltó y cómo mejorar tu argumentación.')
    );
    
    featuresSection.append(featuresHeader, features);
    homepage.append(featuresSection);

    // Footer
    const footer = el('div', { className: 'ae-homepage-footer' });
    footer.append(
      el('p', { textContent: 'adventurengine — Motor web de aventuras educativas' }),
      el('p', { 
        innerHTML: '<a href="https://github.com/sharpccess/adventurengine" target="_blank" rel="noopener">GitHub</a> • <a href="/agentes.html" target="_blank" rel="noopener">Guía para agentes</a> • <a href="https://engine.lumbre.lat" target="_blank" rel="noopener">Demo en vivo</a>'
      })
    );
    homepage.append(footer);

    wrap.append(homepage);
    this.rootEl.append(wrap);

    // Load adventures
    this.populateAdventureGrid(grid);
  }

  private createStat(value: string, label: string): HTMLElement {
    const stat = el('div', { className: 'ae-stat' });
    stat.append(
      el('div', { className: 'ae-stat-value', textContent: value }),
      el('div', { className: 'ae-stat-label', textContent: label })
    );
    return stat;
  }

  private createFeature(icon: string, title: string, description: string): HTMLElement {
    const feature = el('div', { className: 'ae-feature' });
    feature.append(
      el('span', { className: 'ae-feature-icon', textContent: icon }),
      el('h4', { textContent: title }),
      el('p', { textContent: description })
    );
    return feature;
  }

  private async populateAdventureGrid(grid: HTMLElement): Promise<void> {
    // Load API adventures
    let apiEntries: AdventureEntry[] = [];
    let apiError: string | null = null;

    try {
      const res = await fetch('/api/adventures');
      if (res.ok) {
        const data = await res.json() as { adventures: ApiAdventureMeta[] };
        const demoIds = new Set(this.demos.map((d) => d.id));

        for (const meta of data.adventures) {
          if (demoIds.has(meta.id)) continue;

          try {
            const fullRes = await fetch(`/api/adventures/${encodeURIComponent(meta.id)}`);
            if (fullRes.ok) {
              const def = (await fullRes.json()) as GameDefinition;
              apiEntries.push({ id: meta.id, def });
            }
          } catch {
            // Skip adventures that can't be loaded
          }
        }
        this.apiAdventures.length = 0;
        this.apiAdventures.push(...apiEntries);
      } else {
        apiError = `HTTP ${res.status}`;
      }
    } catch {
      apiError = 'No hay conexión con el servidor';
    }

    // Remove loading state
    clear(grid);

    // Combine demos and API adventures
    const allAdventures = [...this.demos, ...apiEntries];

    if (allAdventures.length === 0) {
      const empty = el('div', { className: 'ae-empty' });
      empty.append(
        el('div', { className: 'ae-empty-icon', textContent: '📭' }),
        el('p', { textContent: apiError ? `${apiError}. Solo se muestran las demos.` : 'Aún no hay aventuras disponibles.' })
      );
      grid.append(empty);
      return;
    }

    // Render adventure cards
    for (const entry of allAdventures) {
      grid.append(this.renderAdventureCard(entry));
    }
  }

  private renderAdventureCard(entry: AdventureEntry): HTMLElement {
    const meta = entry.def.metadata;
    const card = el('div', { className: 'ae-adventure-card', attrs: { tabindex: '0' } });
    
    // Image
    const imageContainer = el('div', { className: 'ae-card-image' });
    const coverKey = entry.def.assets.images?.['__cover']?.src;
    if (coverKey) {
      const img = el('img', { src: coverKey, alt: `Ilustración de ${meta.title}` });
      imageContainer.append(img);
    }
    
    // Badge
    const badge = el('div', { className: 'ae-card-badge', textContent: 'Jugar' });
    imageContainer.append(badge);
    
    card.append(imageContainer);

    // Content
    const content = el('div', { className: 'ae-card-content' });
    content.append(el('h3', { className: 'ae-card-title', textContent: meta.title }));
    content.append(el('p', { className: 'ae-card-description', textContent: meta.description }));

    const prov = meta.provenance;
    if (prov && (prov.sourceTitle || prov.templateId)) {
      const box = el('div', { className: 'ae-card-provenance' });
      box.append(el('span', { className: 'ae-prov-label', textContent: 'Origen' }));
      if (prov.sourceTitle) {
        const line = el('div', { className: 'ae-prov-line' });
        if (prov.sourceUrl) {
          line.append(el('a', {
            className: 'ae-prov-link',
            href: prov.sourceUrl,
            target: '_blank',
            rel: 'noopener',
            textContent: prov.sourceTitle
          }));
        } else {
          line.append(el('span', { textContent: prov.sourceTitle }));
        }
        box.append(line);
      }
      if (prov.templateId) {
        box.append(el('div', {
          className: 'ae-prov-line',
          textContent: `Plantilla: ${prov.templateLabel ?? prov.templateId}`
        }));
      }
      content.append(box);
    }

    // Meta info
    const metaInfo = el('div', { className: 'ae-card-meta' });
    const sceneCount = Object.keys(entry.def.scenes).length;
    const evidenceCount = Object.keys(entry.def.evidence).length;
    const endingCount = Object.keys(entry.def.endings).length;
    metaInfo.append(
      el('span', { textContent: `${sceneCount} escenas` }),
      el('span', { textContent: `${evidenceCount} evidencias` }),
      el('span', { textContent: `${endingCount} finales` })
    );
    content.append(metaInfo);

    // Objectives preview
    const objectives = meta.educationalObjectives ?? [];
    if (objectives.length > 0) {
      const list = el('ul', { className: 'ae-card-objectives' });
      const preview = objectives.slice(0, 2);
      for (const obj of preview) {
        list.append(el('li', { textContent: obj }));
      }
      if (objectives.length > 2) {
        list.append(el('li', { textContent: `+${objectives.length - 2} objetivos más` }));
      }
      content.append(list);
    }

    // Actions
    const actions = el('div', { className: 'ae-card-actions' });
    const playBtn = button('▶ Jugar', () => this.renderCover(entry.def), { className: 'ae-btn primary' });
    actions.append(playBtn);
    content.append(actions);

    card.append(content);

    // Click handler for the whole card
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.ae-btn')) return;
      this.renderCover(entry.def);
    });
    card.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.renderCover(entry.def);
      }
    });

    return card;
  }

  private async onLocalFile(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const def = JSON.parse(text) as GameDefinition;
      this.renderCover(def, true);
    } catch (err) {
      window.alert(`No se pudo leer el JSON: ${String((err as Error).message ?? err)}`);
    }
  }

  /* ------------------------- portada ------------------------- */

  private renderCover(def: GameDefinition, isLocal = false): void {
    this.destroyHandle();
    clear(this.rootEl);
    const meta = def.metadata;
    const saves = this.saveManagerFor(def);
    const hasSave = saves.has();

    const wrap = el('div', { className: 'ae-root', attrs: { style: 'overflow:auto' } });
    const modal = el('div', { className: 'ae-modal' });

    const coverKey = def.assets.images?.['__cover']?.src;
    if (coverKey) {
      modal.append(el('img', { className: 'ae-cover-art', src: coverKey, alt: `Ilustración de ${meta.title}` }));
    }
    modal.append(el('h1', { textContent: meta.title }));
    modal.append(el('p', { className: 'lead', textContent: meta.description }));

    modal.append(el('h2', { textContent: 'Objetivos educativos' }));
    const ul = el('ul', { className: 'ae-list' });
    for (const o of meta.educationalObjectives ?? []) ul.append(el('li', { textContent: o }));
    modal.append(ul);

    const row = el('div', { className: 'ae-row' });
    row.append(button('Comenzar', () => this.launch(def, false), { className: 'ae-btn primary' }));
    row.append(button('Continuar', () => this.launch(def, true), { disabled: !hasSave }));
    if (hasSave) {
      row.append(button('Borrar partida', () => {
        this.confirm('¿Borrar la partida guardada de esta aventura? Esta acción no se puede deshacer.', () => {
          this.saveManagerFor(def).clear();
          this.renderCover(def, isLocal);
        });
      }, { className: 'ae-btn danger' }));
    }
    modal.append(row);
    modal.append(el('div', { className: 'ae-row' }, [button('← Volver al selector', () => this.renderSelector())]));

    wrap.append(el('div', { className: 'ae-modal-backdrop' }, [modal]));
    this.rootEl.append(wrap);
  }

  private renderError(title: string, detail: string, onBack: () => void): void {
    this.destroyHandle();
    clear(this.rootEl);
    const wrap = el('div', { className: 'ae-root', attrs: { style: 'overflow:auto;padding:24px' } });
    const card = el('div', { className: 'ae-modal' });
    card.append(el('h1', { textContent: title }));
    card.append(el('p', { className: 'lead', textContent: detail }));
    card.append(el('div', { className: 'ae-row' }, [button('← Volver al selector', onBack)]));
    wrap.append(el('div', { className: 'ae-modal-backdrop', attrs: { style: 'position:static;background:none;padding:0' } }, [card]));
    this.rootEl.append(wrap);
  }

  private confirm(message: string, onYes: () => void): void {
    if (window.confirm(message)) onYes();
  }

  /* ------------------------- juego ------------------------- */

  private async launch(def: GameDefinition, resume: boolean): Promise<void> {
    this.destroyHandle();
    clear(this.rootEl);
    const container = el('div', { className: 'ae-root', attrs: { style: 'height:100vh' } });
    this.rootEl.append(container);
    this.handle = await mountAdventureEngine({
      container,
      gameSource: def,
      instanceId: this.instanceId,
      assetBaseUrl: '',
      reducedMotion: this.reducedMotion,
      resume,
      onExit: () => this.renderCover(def)
    });
  }

  private destroyHandle(): void {
    if (this.handle) {
      this.handle.destroy();
      this.handle = null;
    }
  }
}
